import React, { useState } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import type {
  Triage,
  PatientRow
} from "src/providers/compliance/complianceTypes";
import { TRIAGE_LABELS } from "src/providers/compliance/complianceTypes";
import type {
  MissingDataStream,
  TriageConfig
} from "src/providers/compliance/complianceFilters";
import { triageGroupColors, COMPLIANCE_COLORS } from "./complianceColors";
import { PatientListRow } from "./PatientListRow";

interface TriageGroupProps {
  triage: Triage;
  patients: PatientRow[];
  onPatientClick: (patientId: string) => void;
  selectedPatientId: string | null;
  visibleStreams: MissingDataStream[];
  focusedStream: MissingDataStream | null;
  triageConfig: TriageConfig;
  now: Date;
}

// Not memoized: groups get fresh arrays on every change; rows are memoized instead.
export function TriageGroup({
  triage,
  patients,
  onPatientClick,
  selectedPatientId,
  visibleStreams,
  focusedStream,
  triageConfig,
  now
}: TriageGroupProps): JSX.Element {
  const [open, setOpen] = useState(true);
  const { bg, fg, dot } = triageGroupColors(triage);

  return (
    <Box role="rowgroup">
      {/* A row, so the table's ARIA structure stays valid */}
      <Box role="row">
        <Box role="rowheader">
          <Box
            role="button"
            tabIndex={0}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpen(!open);
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: "13px",
              cursor: "pointer",
              backgroundColor: bg,
              borderBottom: `1px solid ${COMPLIANCE_COLORS.BORDER_ROW}`,
              "&:hover": { filter: "brightness(0.97)" }
            }}
          >
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: "2px",
                backgroundColor: dot,
                flexShrink: 0
              }}
            />
            <Typography
              sx={{ fontSize: 14, fontWeight: 600, color: fg, flex: 1 }}
            >
              {TRIAGE_LABELS[triage]} ({patients.length})
            </Typography>
            {open ? (
              <ExpandLess sx={{ fontSize: 18, color: fg, opacity: 0.6 }} />
            ) : (
              <ExpandMore sx={{ fontSize: 18, color: fg, opacity: 0.6 }} />
            )}
          </Box>
        </Box>
      </Box>
      <Collapse in={open}>
        {patients.map((p) => (
          <PatientListRow
            key={p.patientId}
            patient={p}
            onClick={onPatientClick}
            // A boolean, not the id, so only the old and new selected rows re-render.
            selected={p.patientId === selectedPatientId}
            visibleStreams={visibleStreams}
            focusedStream={focusedStream}
            triageConfig={triageConfig}
            now={now}
          />
        ))}
      </Collapse>
    </Box>
  );
}
